import Backdrop from '@material-ui/core/Backdrop';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Fade from '@material-ui/core/Fade';
import LinearProgress from '@material-ui/core/LinearProgress';
import Modal from '@material-ui/core/Modal';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import React from 'react';
import { connect } from 'react-redux';

import LaserficheCredentials from '../models/LaserficheCredentials';
import { signInUser } from '../store/actions';
import { State } from '../store/reducer';
import { getLaserficheSignInInfo, getSignInDialogState } from '../store/selectors';

export interface StateProps {
  open: boolean;
  errorMessage: string;
  isLoading: boolean;
  laserficheSignInInfo: Pick<LaserficheCredentials, 'username' | 'password'>;
}

export interface DispatchProps {
  signIn: (stateUsername: string, statePassword: string) => void;
}

type Props = StateProps & DispatchProps;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    modal: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    paper: {
      backgroundColor: theme.palette.background.paper,
      '&:focus': {
        outline: 0
      },
      borderRadius: theme.shape.borderRadius,
      boxShadow: theme.shadows[5],
      padding: theme.spacing(2, 3)
    }
  })
);

export const SignInModal: React.FC<Props> = ({
  signIn,
  open,
  errorMessage,
  isLoading,
  laserficheSignInInfo
}) => {
  const classes = useStyles();
  const { username, password } = laserficheSignInInfo;
  const [stateUsername, setStateUsername] = React.useState(username);
  const [statePassword, setStatePassword] = React.useState(password);

  const onPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStatePassword(e.target.value);
  };
  const onUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStateUsername(e.target.value);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    signIn(stateUsername, statePassword);
  };

  return (
    <Box component="section">
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        className={classes.modal}
        open={open}
        closeAfterTransition={false}
        disableBackdropClick={true}
        disableEscapeKeyDown={true}
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500
        }}
      >
        <Fade in={open}>
          <form className={classes.paper} onSubmit={handleSubmit} noValidate={true} autoComplete="off">
            <Box display="flex" flexDirection="column">
              <Typography align="center" component="h4" variant="subtitle1">
                Sign In to Laserfiche
              </Typography>
              <Typography align="center" component="p" color="error">
                {errorMessage}
              </Typography>
              <Box pt={1}>
                <TextField
                  label="Username"
                  margin="dense"
                  fullWidth={true}
                  defaultValue={username}
                  onChange={onUsernameChange}
                />
                <TextField
                  type="password"
                  label="Password"
                  margin="dense"
                  fullWidth={true}
                  defaultValue={password}
                  onChange={onPasswordChange}
                />
              </Box>
              <Box pt={3} pb={1}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth={true}
                  disabled={isLoading}
                >
                  Sign In
                </Button>
              </Box>
              {isLoading && <LinearProgress />}
            </Box>
          </form>
        </Fade>
      </Modal>
    </Box>
  );
};

const mapDispatch = {
  signIn: signInUser
};

export const mapState = (state: State): StateProps => {
  const { open, errorMessage, isLoading } = getSignInDialogState(state);
  const laserficheSignInInfo = getLaserficheSignInInfo(state);
  return { open, errorMessage, isLoading, laserficheSignInInfo };
};

export default connect<StateProps, DispatchProps, {}, State>(
  mapState,
  mapDispatch
)(SignInModal);
